import axiosInstance from '../../config/axiosInstance';
import { isJwtExpired } from './tokenUtils';

export interface LoginCredentials {
    email: string;
    password: string;
}

export interface RegisterData {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
}

export interface AuthResponse {
    user: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        role: string;
        profileImage?: string | null;
        phoneNumber?: string | null;
        gender?: string | null;
        dateOfBirth?: string | null;
        title?: string | null;
        age?: number | null;
        weight?: number | null;
        height?: number | null;
        address?: string | null;
        city?: string | null;
        province?: string | null;
        fcMax?: number | null;
        fcRiposo?: number | null;
        fcSoglia?: number | null;
        restDay?: string | null;
        notes?: string | null;
    };
    accessToken: string;
    refreshToken: string;
}

export interface ResetPasswordData {
    email: string;
}

class JWTService {
    private readonly allowedRoles = ['user', 'trainer'];
    /**
     * Login with email and password
     */
    async login(credentials: LoginCredentials): Promise<AuthResponse> {
        try {
            const response = await axiosInstance.post<AuthResponse>('/auth/login', credentials);

            if (!this.allowedRoles.includes(response.data.user.role)) {
                throw new Error('Access denied. Only members and trainers can access this portal. Admins must use the admin panel.');
            }

            // Store tokens in localStorage
            localStorage.setItem('auth', JSON.stringify({
                user: response.data.user,
                token: response.data.accessToken,
                refreshToken: response.data.refreshToken,
            }));

            return response.data;
        } catch (error: any) {
            if (!error.response) {
                throw new Error('Unable to reach the server. Please check your connection and try again.');
            }

            const message = error.response?.data?.message;
            if (Array.isArray(message)) {
                throw new Error(message.join(', '));
            }
            if (typeof message === 'string' && message.trim().length > 0) {
                throw new Error(message);
            }

            throw new Error(error.message || 'Login failed');
        }
    }

    /**
     * Register new user
     */
    async register(data: RegisterData): Promise<AuthResponse> {
        try {
            const response = await axiosInstance.post<AuthResponse>('/auth/register', data);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Registration failed');
        }
    }

    /**
     * Logout user
     */
    async logout(): Promise<void> {
        try {
            await axiosInstance.post('/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Always clear local storage
            localStorage.removeItem('auth');
        }
    }

    /**
     * Request password reset
     */
    async forgotPassword(data: ResetPasswordData): Promise<{ message: string }> {
        try {
            const response = await axiosInstance.post('/auth/forgot-password', data);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Password reset request failed');
        }
    }

    /**
     * Reset password with token
     */
    async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
        try {
            const response = await axiosInstance.post('/auth/reset-password', {
                token,
                password: newPassword,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Password reset failed');
        }
    }

    /**
     * Verify JWT token
     */
    async verifyToken(): Promise<boolean> {
        try {
            const response = await axiosInstance.get('/auth/verify');
            return response.status === 200;
        } catch (error) {
            return false;
        }
    }

    /**
     * Refresh JWT token
     */
    async refreshToken(): Promise<{ token: string }> {
        try {
            const response = await axiosInstance.post('/auth/refresh');
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Token refresh failed');
        }
    }

    /**
     * Get current user profile
     */
    async getCurrentUser(): Promise<AuthResponse['user']> {
        try {
            const response = await axiosInstance.get('/auth/me');
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch user profile');
        }
    }

    /**
     * Update user profile
     */
    async updateProfile(data: Partial<AuthResponse['user']>): Promise<AuthResponse['user']> {
        try {
            const response = await axiosInstance.put('/auth/profile', data);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Profile update failed');
        }
    }

    /**
     * Upload profile picture
     */
    async uploadProfileImage(file: File): Promise<AuthResponse['user']> {
        try {
            const formData = new FormData();
            formData.append('photo', file);
            const response = await axiosInstance.post('/auth/profile/photo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Photo upload failed');
        }
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        const authData = localStorage.getItem('auth');
        if (!authData) return false;

        try {
            const { token } = JSON.parse(authData);
            return !!token && !isJwtExpired(token);
        } catch (error) {
            return false;
        }
    }

    /**
     * Get stored token
     */
    getToken(): string | null {
        const authData = localStorage.getItem('auth');
        if (!authData) return null;

        try {
            const { token } = JSON.parse(authData);
            if (!token || isJwtExpired(token)) {
                localStorage.removeItem('auth');
                return null;
            }
            return token;
        } catch (error) {
            return null;
        }
    }

    /**
     * Get stored user
     */
    getUser(): AuthResponse['user'] | null {
        const authData = localStorage.getItem('auth');
        if (!authData) return null;

        try {
            const { user } = JSON.parse(authData);
            return user;
        } catch (error) {
            return null;
        }
    }
}

// Export singleton instance
const jwtService = new JWTService();
export default jwtService;
